import { useEffect, useRef, useState } from 'react'
import { useI18n } from 'gunsi-auth'
import { useTally } from '../hooks/useTally.jsx'
import { useTallyI18n } from '../i18n/tally.js'
import { applyTheme, loadThemePrefs, saveThemePrefs } from '../theme.js'
import { CurrencySelect } from '../components/CurrencySelect.jsx'
import { saveSettings, syncLedgerBackfill, syncLedgerWipe } from '../api.js'

export function Settings() {
  const t = useTallyI18n()
  const { locale, setLocale } = useI18n()
  const {
    data, setCurrency, setCalendar, setYearEra, setDayCalendar, setDateFormat,
    setLedgerLinkEnabled, setLedgerCategoryId, setLedgerNameTemplate,
    clearTxCache,
    replaceAll, resetAll,
  } = useTally()
  const [prefs, setPrefs] = useState(loadThemePrefs())
  const [resetOpen, setResetOpen] = useState(false)
  const [ledgerBusy, setLedgerBusy] = useState(false)

  // Toggle handler: persists the flag immediately (bypassing the 500 ms
  // settings debounce) so the backfill/wipe call sees the new value.
  async function toggleLedgerLink(enabled) {
    setLedgerBusy(true)
    try {
      setLedgerLinkEnabled(enabled)
      await saveSettings({ ledgerLinkEnabled: enabled })
      if (enabled) await syncLedgerBackfill()
      else         await syncLedgerWipe()
      clearTxCache?.()
    } catch (e) {
      console.error('[settings] toggleLedgerLink failed', e)
    } finally {
      setLedgerBusy(false)
    }
  }

  // Ledger quotas mirror as income tx in Tally, so pick from income categories
  const incomeCats = (data.categories ?? []).filter(c => c.type === 'income')

  // Explicit save+backfill on settings change. Bypasses useTally's 500 ms
  // settings-save debounce so the backfill always reads the *new* value.
  // (The previous useEffect-debounce approach was cancelled by the cleanup
  // when the user navigated away from Settings before it fired.)
  async function saveAndResyncLedger(patch) {
    setLedgerBusy(true)
    try {
      await saveSettings(patch)
      if (data.ledgerLinkEnabled || patch.ledgerLinkEnabled) {
        await syncLedgerBackfill()
        clearTxCache?.()
      }
    } catch (e) {
      console.error('[settings] saveAndResyncLedger failed', e)
    } finally {
      setLedgerBusy(false)
    }
  }
  function changeLedgerCategory(newId) {
    setLedgerCategoryId(newId)
    saveAndResyncLedger({ ledgerCategoryId: newId || null })
  }
  // Track the last-saved template so onBlur only triggers when it actually changed
  const lastSavedTemplateRef = useRef(null)
  if (lastSavedTemplateRef.current === null) lastSavedTemplateRef.current = data.ledgerNameTemplate
  function commitLedgerNameTemplate() {
    const v = data.ledgerNameTemplate ?? ''
    if (v === lastSavedTemplateRef.current) return
    lastSavedTemplateRef.current = v
    saveAndResyncLedger({ ledgerNameTemplate: v })
  }

  function updateTheme(theme, colorSet) {
    saveThemePrefs(theme, colorSet)
    applyTheme(theme, colorSet)
    setPrefs({ theme, colorSet })
  }

  function exportJson() {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href = url
    a.download = `gunsi-tally-${new Date().toISOString().slice(0, 10)}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  function importJson(e) {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result))
        replaceAll(parsed)
      } catch {
        alert('Invalid JSON')
      }
    }
    reader.readAsText(file)
    e.target.value = ''
  }

  function handleConfirmedReset() {
    resetAll()
    setResetOpen(false)
  }

  return (
    <div className="view">
      <header className="view-header"><h1 className="view-title">{t.settings.title}</h1></header>

      <section className="card">
        <h2>{t.settings.appearance}</h2>
        <div className="form-row">
          <label>
            {t.settings.theme}
            <select value={prefs.theme} onChange={e => updateTheme(e.target.value, prefs.colorSet)}>
              <option value="system">{t.settings.themes.system}</option>
              <option value="light">{t.settings.themes.light}</option>
              <option value="dark">{t.settings.themes.dark}</option>
            </select>
          </label>
          <label>
            {t.settings.colorSet}
            <select value={prefs.colorSet} onChange={e => updateTheme(prefs.theme, e.target.value)}>
              <option value="modern">{t.settings.colorSets.modern}</option>
              <option value="classic">{t.settings.colorSets.classic}</option>
            </select>
          </label>
        </div>
      </section>

      <section className="card" style={{ marginTop: '1rem' }}>
        <h2>{t.settings.currency} / {t.settings.language}</h2>
        <div className="form-row">
          <label>
            {t.settings.currency}
            <CurrencySelect
              value={data.currency}
              onChange={setCurrency}
              searchPlaceholder={t.common.searchCurrency}
              title={t.settings.currency}
              closeLabel={t.common.close}
            />
          </label>
          <label>
            {t.settings.language}
            <select value={locale ?? 'en'} onChange={e => setLocale(e.target.value)}>
              <option value="en">English</option>
              <option value="it">Italiano</option>
            </select>
          </label>
        </div>
      </section>

      <section className="card" style={{ marginTop: '1rem' }}>
        <h2>{t.settings.calendar}</h2>
        <div className="settings-stack">
          <label>
            {t.settings.calendar}
            <select value={data.calendar} onChange={e => setCalendar(e.target.value)}>
              <option value="gregorian">{t.settings.calendars.gregorian}</option>
              <option value="lil">{t.settings.calendars.lil}</option>
            </select>
          </label>
          <label>
            {t.settings.dateFormat}
            <select value={data.dateFormat ?? 'auto'} onChange={e => setDateFormat(e.target.value)}>
              <option value="auto">{t.settings.dateFormats.auto}</option>
              <option value="dmy">{t.settings.dateFormats.dmy}</option>
              <option value="mdy">{t.settings.dateFormats.mdy}</option>
              <option value="ymd">{t.settings.dateFormats.ymd}</option>
            </select>
          </label>
          {data.calendar === 'lil' && (
            <>
              <label>
                {t.settings.yearEra}
                <select value={data.yearEra} onChange={e => setYearEra(e.target.value)}>
                  <option value="lil">{t.settings.yearEras.lil}</option>
                  <option value="he">{t.settings.yearEras.he}</option>
                </select>
              </label>
              <label>
                {t.settings.dayCalendar}
                <select value={data.dayCalendar} onChange={e => setDayCalendar(e.target.value)}>
                  <option value="lil">{t.settings.dayCalendars.lil}</option>
                  <option value="gregorian">{t.settings.dayCalendars.gregorian}</option>
                </select>
              </label>
            </>
          )}
        </div>
      </section>

      <section className="card" style={{ marginTop: '1rem' }}>
        <h2>{t.settings.ledger.title}</h2>
        <label className="settings-toggle-row">
          <input
            type="checkbox"
            checked={Boolean(data.ledgerLinkEnabled)}
            disabled={ledgerBusy}
            onChange={e => toggleLedgerLink(e.target.checked)}
          />
          <div className="settings-toggle-text">
            <span className="settings-toggle-label">{t.settings.ledger.toggle}</span>
            <span className="settings-toggle-hint">{t.settings.ledger.hint}</span>
          </div>
        </label>

        {data.ledgerLinkEnabled && (
          <div className="settings-stack" style={{ marginTop: '0.5rem' }}>
            <label>
              {t.settings.ledger.category}
              <select
                value={data.ledgerCategoryId ?? ''}
                disabled={ledgerBusy}
                onChange={e => changeLedgerCategory(e.target.value || null)}
              >
                <option value="">{t.settings.ledger.categoryNone}</option>
                {incomeCats.map(c => (
                  <option key={c.id} value={c.id}>{c.icon} {c.name}</option>
                ))}
              </select>
            </label>
            <label>
              {t.settings.ledger.nameTemplate}
              <input
                type="text"
                value={data.ledgerNameTemplate ?? ''}
                disabled={ledgerBusy}
                onChange={e => setLedgerNameTemplate(e.target.value)}
                onBlur={commitLedgerNameTemplate}
                onKeyDown={e => { if (e.key === 'Enter') e.target.blur() }}
                placeholder="Quota {periodLabel}"
              />
              <span className="settings-hint">
                {t.settings.ledger.templateHint.replace('{placeholder}', '{periodLabel}')}
              </span>
            </label>
          </div>
        )}
      </section>

      <section className="card" style={{ marginTop: '1rem' }}>
        <h2>{t.settings.data}</h2>
        <div className="settings-stack">
          <button className="btn" onClick={exportJson}>{t.settings.exportJson}</button>
          <label className="btn">
            {t.settings.importJson}
            <input type="file" accept="application/json" onChange={importJson} hidden />
          </label>
          <button className="btn btn-danger" onClick={() => setResetOpen(true)}>
            {t.settings.reset}
          </button>
        </div>
      </section>

      {resetOpen && (
        <ResetModal
          t={t}
          onCancel={() => setResetOpen(false)}
          onConfirm={handleConfirmedReset}
        />
      )}
    </div>
  )
}

function ResetModal({ t, onCancel, onConfirm }) {
  const [step, setStep]   = useState(1)
  const [typed, setTyped] = useState('')
  const phrase = t.settings.resetConfirmPhrase
  const matches = typed.trim().toUpperCase() === phrase.toUpperCase()

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal modal-danger" onClick={e => e.stopPropagation()}>
        <h2>{t.settings.resetTitle}</h2>
        <p className="modal-warning-text">{t.settings.resetWarning}</p>

        {step === 1 ? (
          <div className="modal-actions">
            <button type="button" className="btn" onClick={onCancel}>
              {t.common.cancel}
            </button>
            <button type="button" className="btn btn-danger" onClick={() => setStep(2)}>
              {t.settings.resetContinue}
            </button>
          </div>
        ) : (
          <>
            <label>
              {t.settings.resetTypePrompt.replace('{phrase}', phrase)}
              <input
                type="text"
                value={typed}
                onChange={e => setTyped(e.target.value)}
                placeholder={phrase}
                autoFocus
                autoComplete="off"
                spellCheck={false}
              />
            </label>
            <div className="modal-actions">
              <button type="button" className="btn" onClick={onCancel}>
                {t.common.cancel}
              </button>
              <button
                type="button"
                className="btn btn-danger"
                disabled={!matches}
                onClick={onConfirm}
              >
                {t.settings.resetFinal}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
